import React, { useId } from "react";
import "./Field.scss";

export type FieldProps = {
  label?: React.ReactNode;
  /** Ties the label to the control; generated when left out (pass it on to the control via the render prop) */
  htmlFor?: string;
  /** Muted help under the control */
  hint?: React.ReactNode;
  /** A red message under the control */
  error?: React.ReactNode;
  required?: boolean;
  /** Something at the right of the label row (a counter, a "Clear" link…) */
  aside?: React.ReactNode;
  className?: string;
  children: React.ReactNode | ((id: string) => React.ReactNode);
};

/** A form row: label, control, hint or error. */
export function Field({ label, htmlFor, hint, error, required, aside, className, children }: FieldProps): React.JSX.Element {
  const generated = useId();
  const id = htmlFor ?? generated;
  return (
    <div className={["ui-field", error ? "ui-field--error" : "", className].filter(Boolean).join(" ")}>
      {label || aside ? (
        <div className="ui-field__label-row">
          {label ? (
            <label className="ui-field__label" htmlFor={id}>
              {label}
              {required ? <span className="ui-field__required" aria-hidden="true">*</span> : null}
            </label>
          ) : null}
          {aside ? <span className="ui-field__aside">{aside}</span> : null}
        </div>
      ) : null}
      {typeof children === "function" ? children(id) : children}
      {error ? (
        <p className="ui-field__error" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="ui-field__hint">{hint}</p>
      ) : null}
    </div>
  );
}

type InputOwnProps = {
  /** sm 32px, md 40px (default) */
  inputSize?: "sm" | "md";
  invalid?: boolean;
  /** An icon inside the field, at the left */
  leftIcon?: React.ReactNode;
  /** Text or a button inside the field, at the right (a unit: "EGP", "%"…) */
  rightSlot?: React.ReactNode;
  /** Full width (default true) */
  block?: boolean;
};

export type InputProps = React.InputHTMLAttributes<HTMLInputElement> & InputOwnProps;

/** A text-like input (text, email, number, date, time, password…) in the brand style. */
export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { inputSize = "md", invalid = false, leftIcon, rightSlot, block = true, className, ...rest },
  ref
) {
  const classes = ["ui-input", `ui-input--${inputSize}`, invalid ? "is-invalid" : "", block ? "ui-input--block" : "", className]
    .filter(Boolean)
    .join(" ");
  if (!leftIcon && !rightSlot) {
    return <input {...rest} ref={ref} aria-invalid={invalid || rest["aria-invalid"] || undefined} className={classes} />;
  }
  return (
    <div className={["ui-input-wrap", block ? "ui-input-wrap--block" : "", rest.disabled ? "is-disabled" : ""].filter(Boolean).join(" ")}>
      {leftIcon ? <span className="ui-input-wrap__left">{leftIcon}</span> : null}
      <input
        {...rest}
        ref={ref}
        aria-invalid={invalid || rest["aria-invalid"] || undefined}
        className={[classes, leftIcon ? "has-left" : "", rightSlot ? "has-right" : ""].filter(Boolean).join(" ")}
      />
      {rightSlot ? <span className="ui-input-wrap__right">{rightSlot}</span> : null}
    </div>
  );
});

export type TextareaProps = React.TextareaHTMLAttributes<HTMLTextAreaElement> & { invalid?: boolean };

export const Textarea = React.forwardRef<HTMLTextAreaElement, TextareaProps>(function Textarea({ invalid = false, className, rows = 4, ...rest }, ref) {
  return (
    <textarea
      {...rest}
      ref={ref}
      rows={rows}
      aria-invalid={invalid || rest["aria-invalid"] || undefined}
      className={["ui-input", "ui-textarea", "ui-input--block", invalid ? "is-invalid" : "", className].filter(Boolean).join(" ")}
    />
  );
});

/** Form fields laid out in columns that fold to one on narrow screens. */
export function FormGrid({ columns = 2, className, children }: { columns?: 1 | 2 | 3 | 4; className?: string; children: React.ReactNode }) {
  return <div className={["ui-form-grid", `ui-form-grid--${columns}`, className].filter(Boolean).join(" ")}>{children}</div>;
}

export default Field;
